// src/routes/AppRouter.jsx
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import PrivateRoute from "../auth/PrivateRoute";
import Login from "../auth/Login";
import Signup from "../auth/Signup";
import ForgotPassword from "../auth/ForgotPassword";
import PasswordResetSent from "../auth/PasswordResetSent";
import ResetPassword from "../auth/ResetPassword";
import Layout from "../components/Layout";
import Home from "../pages/Home";
import UserList from "../pages/admin/UserList";
import UserForm from "../pages/admin/UserForm";
import AnimalList from "../pages/animals/AnimalList";
import AnimalProfile from "../pages/animals/AnimalProfile";
import AnimalForm from "../pages/animals/AnimalForm";
import AnimalEdit from "../pages/animals/AnimalEdit";
import TreatmentList from "../pages/treatments/TreatmentList";
import TreatmentForm from "../pages/treatments/TreatmentForm";
import TreatmentDetail from "../pages/treatments/TreatmentDetail";
import TreatmentEdit from "../pages/treatments/TreatmentEdit";
import MedicineCabinet from "../pages/medicines/MedicineCabinet";
import RequireRole from "../auth/RequireRole";
import RoleForm from "../components/admin/RoleForm";
import RoleList from "../components/admin/RoleList";
import TenantForm from "../pages/admin/TenantForm";
import TenantList from "../pages/admin/TenantList";
import TenantDetail from "../pages/admin/TenantDetail";
import UserProfile from "../pages/admin/UserProfile";
import CheckEmail from "../pages/onboarding/CheckEmail";
import VerifyEmail from "../pages/onboarding/VerifyEmail";
import AcceptInvite from "../pages/onboarding/AcceptInvite";
import CreateFarm from "../pages/onboarding/CreateFarm";
import FarmSetup from "../pages/onboarding/FarmSetup";
import AdminInvites from "../pages/admin/AdminInvites";
import MyProfile from "../pages/account/MyProfile";
import PostAuthHome from "./PostAuthHome";
import Phase1OutOfScopeRedirect from "./Phase1OutOfScopeRedirect";
import PublicRoot from "./PublicRoot";
import { APP_HOME_PATH } from "../config/routes";
import AnimalAttendance from "../pages/animals/AnimalAttendance";
import ActivityList from "../pages/planning/ActivityList";
import ActivityForm from "../pages/planning/ActivityForm";
import ActivityDetail from "../pages/planning/ActivityDetail";
import MilkDashboardPage from "../pages/milking/MilkDashboardPage";

export default function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<PublicRoot />} />
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/password-reset-sent" element={<PasswordResetSent />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/check-email" element={<CheckEmail />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />

        {/* Protected routes with Layout */}
        <Route element={<PrivateRoute />}>
          <Route element={<Layout />}>
            <Route path="dashboard" element={<PostAuthHome />} />
            <Route path="create-farm" element={<CreateFarm />} />
            <Route path="farm-setup" element={<FarmSetup />} />
            <Route path="profile" element={<MyProfile />} />
            <Route path="home" element={<Home />} />
            <Route path="admin">
              <Route
                index
                element={
                  <RequireRole
                    allowedRoles={["super_admin", "TenantAdmin"]}
                    orPermissions={["manage_users"]}
                  >
                    <Navigate to={APP_HOME_PATH} replace />
                  </RequireRole>
                }
              />
              <Route
                path="users/:id"
                element={
                  <RequireRole>
                    <UserProfile />
                  </RequireRole>
                }
              />
              <Route
                path="users"
                element={
                  <RequireRole
                    allowedRoles={["super_admin", "TenantAdmin"]}
                    orPermissions={["manage_users"]}
                  >
                    <UserList />
                  </RequireRole>
                }
              />
              <Route
                path="users/:id/edit"
                element={
                  <RequireRole
                    allowedRoles={["super_admin", "TenantAdmin"]}
                    orPermissions={["manage_users"]}
                  >
                    <UserForm />
                  </RequireRole>
                }
              />
            </Route>
            <Route path="admin/roles">
              <Route
                index
                element={
                  <RequireRole
                    allowedRoles={["super_admin", "TenantAdmin"]}
                    orPermissions={["manage_roles"]}
                  >
                    <RoleList />
                  </RequireRole>
                }
              />
              <Route
                path="create"
                element={
                  <RequireRole
                    allowedRoles={["super_admin", "TenantAdmin"]}
                    orPermissions={["manage_roles"]}
                  >
                    <RoleForm />
                  </RequireRole>
                }
              />
              <Route
                path=":id/edit"
                element={
                  <RequireRole
                    allowedRoles={["super_admin", "TenantAdmin"]}
                    orPermissions={["manage_roles"]}
                  >
                    <RoleForm isEdit />
                  </RequireRole>
                }
              />
            </Route>
            <Route path="admin/invites">
              <Route
                index
                element={
                  <RequireRole
                    allowedRoles={["super_admin", "TenantAdmin"]}
                    orPermissions={["manage_users"]}
                  >
                    <AdminInvites />
                  </RequireRole>
                }
              />
            </Route>
            <Route path="admin/tenants">
              <Route
                index
                element={
                  <RequireRole allowedRoles={["super_admin"]}>
                    <TenantList />
                  </RequireRole>
                }
              />
              <Route
                path="create"
                element={
                  <RequireRole allowedRoles={["super_admin"]}>
                    <TenantForm />
                  </RequireRole>
                }
              />
              <Route
                path=":id"
                element={
                  <RequireRole allowedRoles={["super_admin"]}>
                    <TenantDetail />
                  </RequireRole>
                }
              />
              <Route
                path=":id/edit"
                element={
                  <RequireRole allowedRoles={["super_admin"]}>
                    <TenantForm isEdit />
                  </RequireRole>
                }
              />
            </Route>
            <Route path="/animals" element={<AnimalList />} />
            <Route path="/animal/create_animal" element={<AnimalForm />} />
            <Route path="/animal/:id/edit" element={<AnimalEdit />} />
            <Route path="/animal/:id" element={<AnimalProfile />} />
            <Route path="/treatments" element={<TreatmentList />} />
            <Route path="/medicines" element={<MedicineCabinet />} />
            <Route path="/treatment/create_treatment" element={<TreatmentForm />} />
            <Route path="/treatment/:id/edit" element={<TreatmentEdit />} />
            <Route path="/treatment/:id" element={<TreatmentDetail />} />
            <Route
              path="/milking"
              element={
                <RequireRole
                  allowedRoles={["super_admin", "TenantAdmin"]}
                  orPermissions={["manage_milk", "view_financials"]}
                >
                  <MilkDashboardPage />
                </RequireRole>
              }
            />
            {/* Phase 1: other modules redirect to home */}
            <Route path="milking/*" element={<Phase1OutOfScopeRedirect />} />
            <Route path="income/*" element={<Phase1OutOfScopeRedirect />} />
            <Route path="expenses/*" element={<Phase1OutOfScopeRedirect />} />
            <Route path="stock-taking" element={<AnimalAttendance />} />
            <Route path="calves/*" element={<Phase1OutOfScopeRedirect />} />
            <Route
              path="planning/activities/new"
              element={<ActivityForm />}
            />
            <Route
              path="planning/activities/:id/edit"
              element={<ActivityForm isEdit />}
            />
            <Route path="planning/activities/:id" element={<ActivityDetail />} />
            <Route path="planning/activities" element={<ActivityList />} />
            <Route path="planning/*" element={<Phase1OutOfScopeRedirect />} />
            <Route path="/reports" element={<Phase1OutOfScopeRedirect />} />
            <Route path="reports/*" element={<Phase1OutOfScopeRedirect />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
